// =============================================================
// Auth token — PIN-based, persisted in localStorage.
// Token is sent as `X-Auth-Token` header on REST and as
// `?token=...` on WebSocket connections.
// =============================================================

const TOKEN_KEY = 'pwa_auth_token'

export function getAuthToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setAuthToken(token: string): void {
  if (!token) {
    localStorage.removeItem(TOKEN_KEY)
    return
  }
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export function authHeaders(): Record<string, string> {
  const t = getAuthToken()
  return t ? { 'X-Auth-Token': t } : {}
}

export async function authFetch(url: string, opts: RequestInit = {}): Promise<Response> {
  const headers = new Headers(opts.headers || {})
  for (const [k, v] of Object.entries(authHeaders())) headers.set(k, v)
  return fetch(url, { ...opts, headers })
}

export function authWsUrl(base: string): string {
  const t = getAuthToken()
  if (!t) return base
  return base + (base.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(t)
}

/**
 * Submit PIN to /api/auth and persist returned token.
 * Returns true on success.
 */
export async function submitPin(pin: string): Promise<boolean> {
  const r = await fetch('/api/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  })
  if (!r.ok) return false
  const data = (await r.json()) as { token?: string }
  if (data.token) setAuthToken(data.token)
  return true
}

/**
 * Probe whether we already have a valid session.
 * Returns true if /api/status responds OK with or without a token.
 */
export async function checkAuthStatus(): Promise<'valid' | 'needs-pin'> {
  // First try with stored token
  if (getAuthToken()) {
    const r = await fetch('/api/status', { headers: authHeaders() })
    if (r.ok) return 'valid'
    clearAuthToken()
  }
  // Then try unauthenticated
  const r2 = await fetch('/api/status')
  if (r2.ok) return 'valid'
  return 'needs-pin'
}